import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from "@material-ui/core"
import {
  checkEmail,
  checkPhone,
  createAdmin,
  createSuperAdmin,
} from "actions/Clerk/users"
import ButtonRounded from "components/Common/ButtonRounded"
import AppContext from "contexts/App"
import { Form, Formik } from "formik"
import PropTypes from "prop-types"
import { useEffect, useRef, useState } from "react"
import { useContext } from "react"
import InputMask from "react-input-mask"
import { useMutation, useQueryClient } from "react-query"
import * as Yup from "yup"

import useStyles from "./styles"

const phoneRegex = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/

const schema = Yup.object().shape({
  first_name: Yup.string().required(`Required`),
  last_name: Yup.string().required(`Required`),
  email: Yup.string().email("Please enter a valid email").required(`Required`),
  phone: Yup.string()
    .required("Required")
    .matches(phoneRegex, "Phone number is not valid")
    .min(14, "Phone number is not valid")
    .max(14, "Phone number is not valid"),
  //14 char - 9 numbers, 3 special chars ()-, 1 space
})

const CreateAdminForm = ({
  handleClose,
  setStep,
  setEmailSent,
  fromEntity,
  entityId,
}) => {
  const classes = useStyles()
  const formRef = useRef()
  const createSuperAdminMutation = useMutation(createSuperAdmin)
  const { openSnackBar } = useContext(AppContext)
  const createAdminMutation = useMutation(createAdmin)
  const queryClient = useQueryClient()
  const checkEmailMutation = useMutation(checkEmail)
  const checkPhoneMutation = useMutation(checkPhone)
  const [loading, setLoading] = useState(false)

  const onSubmit = (values) => {
    let plainPhone = formRef?.current?.values?.phone?.replace(/[^0-9]/g, "")
    setLoading(true)
    let emailPromise = new Promise((resolve, reject) => {
      checkEmailMutation
        .mutateAsync(formRef?.current?.values?.email)
        .then((res) => {
          if (res.email_found === true) {
            formRef?.current?.setErrors({
              ...formRef.current.errors,
              email: "Email is already in use",
            })
            reject()
          } else {
            resolve()
          }
        })
    })
    let phonePromise = new Promise((resolve, reject) => {
      let plainPhone = formRef?.current?.values?.phone?.replace(/[^0-9]/g, "")
      checkPhoneMutation.mutateAsync(plainPhone).then((res) => {
        if (res.phone_found === true) {
          formRef?.current?.setErrors({
            ...formRef.current.errors,
            phone: "Phone number is already in use",
          })
          reject()
        } else {
          resolve()
        }
      })
    })
    if (fromEntity) {
      let data = { ...values, phone: plainPhone, entityId: entityId }
      return Promise.all([emailPromise, phonePromise])
        .then(() => {
          createAdminMutation?.mutateAsync(data).then(() => {
            queryClient.setQueryData(["entityData", entityId], (oldData) => {
              let newData = oldData
              newData.admins = [...oldData?.admins, values]
              return newData
            })
            setEmailSent(formRef?.current?.values?.email)
            setStep(1)
            setLoading(false)
          })
          setLoading(false)
        })
        .catch((err) => {
          console.log(err)
          setLoading(false)
        })
    } else {
      let data = { ...values, phone: plainPhone }
      return Promise.all([emailPromise, phonePromise])
        .then(() => {
          createSuperAdminMutation?.mutateAsync(data).then((res) => {
            setEmailSent(formRef?.current?.values?.email)
            setStep(1)
            setLoading(false)
          })
          setLoading(false)
        })
        .catch((err) => {
          console.log(err)
          setLoading(false)
        })
    }
  }

  return (
    <>
      <Formik
        initialValues={{
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
        }}
        onSubmit={onSubmit}
        validationSchema={schema}
        innerRef={formRef}
      >
        {({
          handleChange,
          values,
          errors,
          touched,
          handleBlur,
          isSubmitting,
          handleSubmit,
          setErrors,
        }) => (
          <Form onSubmit={handleSubmit}>
            <DialogContent className={classes.contentContainer}>
              <Typography className={classes.label}>First Name</Typography>
              <TextField
                type="text"
                name="first_name"
                id="first_name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.first_name}
                variant="outlined"
                fullWidth
                style={{ backgroundColor: "#F8F8F8", marginBottom: "20px" }}
                helperText={touched.first_name && errors.first_name}
                error={Boolean(touched.first_name && errors.first_name)}
                FormHelperTextProps={{
                  className: classes.formHelperText,
                }}
              />
              <Typography className={classes.label}>Last Name</Typography>
              <TextField
                type="text"
                name="last_name"
                id="last_name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.last_name}
                variant="outlined"
                fullWidth
                style={{ backgroundColor: "#F8F8F8", marginBottom: "20px" }}
                helperText={touched.last_name && errors.last_name}
                error={Boolean(touched.last_name && errors.last_name)}
                FormHelperTextProps={{
                  className: classes.formHelperText,
                }}
              />
              <Typography className={classes.label}>Phone</Typography>
              <InputMask
                id="phone-mask"
                mask="(999) 999-9999"
                maskChar="_"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.phone}
              >
                {() => (
                  <TextField
                    type="text"
                    name="phone"
                    id="phone"
                    variant="outlined"
                    fullWidth
                    style={{ backgroundColor: "#F8F8F8", marginBottom: "20px" }}
                    helperText={touched.phone && errors.phone}
                    error={Boolean(touched.phone && errors.phone)}
                    FormHelperTextProps={{
                      className: classes.formHelperText,
                    }}
                  />
                )}
              </InputMask>
              <Typography className={classes.label}>Email</Typography>
              <TextField
                type="text"
                name="email"
                id="email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                variant="outlined"
                fullWidth
                style={{ backgroundColor: "#F8F8F8", marginBottom: "20px" }}
                helperText={touched.email && errors.email}
                error={Boolean(touched.email && errors.email)}
                FormHelperTextProps={{
                  className: classes.formHelperText,
                }}
              />
            </DialogContent>
            <DialogActions>
              <Box display="flex" justifyContent="center" width="100%">
                <ButtonRounded
                  color="primary"
                  variant="outlined"
                  onClick={handleClose}
                  type="button"
                >
                  Close
                </ButtonRounded>
                <ButtonRounded
                  color="primary"
                  variant="contained"
                  type="submit"
                  className={classes.saveButton}
                  disabled={loading}
                >
                  {loading ? (
                    <Box width="50px">
                      <CircularProgress size={15} />
                    </Box>
                  ) : (
                    "Create"
                  )}
                </ButtonRounded>
              </Box>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </>
  )
}

CreateAdminForm.propTypes = {
  handleClose: PropTypes.func,
  setStep: PropTypes.func,
  setEmailSent: PropTypes.func,
  fromEntity: PropTypes.bool,
  entityId: PropTypes.number,
}

export default CreateAdminForm
